import { assert } from '@library/guards'

import type { Predicate } from '../predicates'

export const chunkArray = <T>(array: T[], chunkSize: number) =>
  Array.from({ length: Math.ceil(array.length / chunkSize) }, (_, index) => index * chunkSize).map(
    begin => array.slice(begin, begin + chunkSize),
  )

/**
 * Groups elements from an iterable into an object based on a callback function.
 *
 * @template T, K
 * @param {Iterable<T>} iterable - The iterable to group.
 * @param {function(T, number): K} callbackfn - The callback function to
 * determine the grouping key.
 * @returns {Object.<string, T[]>} An object where keys are the grouping keys
 * and values are arrays of grouped elements.
 */
export function groupBy<T extends object, Key extends PropertyKey>(
  iterable: Iterable<T>,
  selector: (item: T, index: number) => Key,
): Partial<Record<Key, T[]>> {
  const obj = Object.create(null) as Record<Key, T[]>
  let i = 0
  for (const value of iterable) {
    const key = selector(value, i++)
    if (key in obj) {
      obj[key].push(value)
    } else {
      obj[key] = [value]
    }
  }
  return obj
}

export function createMap<T extends object, Key extends PropertyKey>(
  iterable: Iterable<T>,
  selector: (item: T, index: number) => Key,
) {
  const obj = Object.create(null) as Partial<Record<Key, T>>
  let i = 0
  for (const value of iterable) {
    const key = selector(value, i++)
    assert(!(key in obj), `Создание словаря из неуникальных ключей ${String(key)}`)
    obj[key] = value
  }
  return obj
}

type Extractor<T> = (value: T) => number

export function minBy<T>(iterable: ArrayLike<T>, extractor: Extractor<T>): T | undefined {
  if (iterable.length === 0) return

  let minIndex = 0
  let minValue = extractor(iterable[0])

  for (let i = 0; i < iterable.length; i++) {
    const value = extractor(iterable[i])
    if (value < minValue) {
      minValue = value
      minIndex = i
    }
  }

  return iterable[minIndex]
}

export function maxBy<T>(iterable: ArrayLike<T>, extractor: Extractor<T>): T | undefined {
  if (iterable.length === 0) return

  let maxIndex = 0
  let maxValue = extractor(iterable[0])

  for (let i = 0; i < iterable.length; i++) {
    const value = extractor(iterable[i])
    if (value > maxValue) {
      maxValue = value
      maxIndex = i
    }
  }

  return iterable[maxIndex]
}

export function count<T>(iterable: ArrayLike<T>, predicate: Predicate<T>): number {
  let count = 0

  for (let i = 0; i < iterable.length; i++) {
    if (predicate(iterable[i])) {
      count += 1
    }
  }

  return count
}

export function sortBy<T>(array: T[], extractor: Extractor<T>, order: 'asc' | 'des' = 'asc') {
  const factor = order === 'asc' ? 1 : -1
  return [...array].sort((one, other) => factor * (extractor(one) - extractor(other)))
}
